import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useRingCentralCall } from '../../hooks/useRingCentralCall';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { setAudioElements } from '../../lib/ringcentral/webphone';
import {
  hangupActiveCall,
  onSessionEvent,
  offSessionEvent,
  isLocalAudioMuted,
  muteLocalAudio,
  unmuteLocalAudio,
  startCallRecording,
  stopCallRecording,
  transferActiveCall,
  warmTransferActiveCall,
} from '../../lib/ringcentral/session';
import {
  ensureAuthorized,
  rememberPendingNumber,
  redirectToAuthorize,
  openAuthorizePopup,
  isCallingSupported,
} from '../../lib/ringcentral/auth';
import RcLoginModal from './RcLoginModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhone,
  faCircle,
  faStop,
  faRightLeft,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';

// `backdrop` + `style` pass through to the underlying Modal so callers that
// layer the dialer OVER another surface (the Onboarding Support slide-over
// keeps its script visible during the call) can lift the z-index and drop
// the dimming backdrop.
// `dockOnNarrow`: below 1100px the dialer renders as a slim call bar docked
// to the bottom of the screen (native call-banner pattern) instead of a
// centered modal, so a full-width surface behind it (the board's slide-over)
// stays completely visible and scrollable during the call. Opt-in — other
// callers keep the modal at every size.
// `onCallPlaced(date)`: optional — fires the instant a call is actually
// dialed (not on open, not on close). The Onboarding Support board uses it
// to stamp the step's SLA at placement rather than at the outcome click.
// `onCallSessionId(id)`: optional — fires ONCE with the RC telephony session
// id (s-...) as soon as the SIP layer exposes it (usually within seconds of
// the call progressing). The board stamps it on the completed step so its
// outside-activity reconcile can exclude page-dialed calls by exact id.
// `fromNumber`: optional caller-ID override for this dialer only (the board
// presents the signed-in person's own line; other surfaces keep the
// app-wide default).
// `transferTargets` (2026-08-10): [{userId, name, extension}] — renders the
// in-call Transfer button when non-empty. Blind ("Send now") forwards and
// drops our leg; warm ("Ask first") holds the caller, dials the target as a
// consult call, then Complete joins them / Cancel returns to the caller.
// `onTransferred(target|null, mode)` fires the moment a transfer is
// COMMITTED (blind fired / warm completed) so the parent can stage its
// post-transfer confirm before the session terminates; null clears a
// staged transfer after a failure.
// `onSendLink` (+ `sendLinkEnabled`): the in-call "Send Link" text — the
// parent owns the API call; disabled with an explanatory tooltip when the
// org scheduling link isn't configured.
const RcCallModal = ({
  show,
  onHide,
  targetNumber,
  hideRecord = false,
  backdrop = 'static',
  style,
  className,
  dialogClassName,
  enforceFocus = true,
  dockOnNarrow = false,
  onCallPlaced,
  onCallSessionId,
  fromNumber,
  transferTargets = [],
  onTransferred,
  onSendLink,
  sendLinkEnabled = false,
}) => {
  const { startCallTo, isPlacing } = useRingCentralCall();
  const [status, setStatus] = useState('idle');
  const isWide = useMediaQuery('(min-width: 1100px)');
  const docked = dockOnNarrow && !isWide;
  // Browser can't call at all (no WebCrypto/mic — plain http on a LAN IP,
  // i.e. phone-testing the dev server). Explain instead of crashing.
  const unsupported = !isCallingSupported();
  const UNSUPPORTED_MSG =
    'Calling needs a secure (HTTPS) connection. This works in production; for local phone testing use an https tunnel.';
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [timerId, setTimerId] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  // Whether a call was actually placed this open — lets the parent decide
  // whether to log a touch (vs. the user dismissing without calling).
  const placedRef = useRef(false);
  // Live transfer state: the target picker panel, the warm-consult call,
  // and a per-consult token so a stale consult's disposed handler can never
  // clobber a newer one.
  const [transferPanel, setTransferPanel] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [consult, setConsult] = useState(null); // {target, complete, cancel}
  const [transferBusy, setTransferBusy] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const consultTokenRef = useRef(null);

  const targetDisplay = useMemo(() => {
    if (!targetNumber) return '';
    const digits = String(targetNumber).replace(/[^\d]/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return targetNumber;
  }, [targetNumber]);

  useEffect(() => {
    if (!show) return;
    (async () => {
      const authorized = await ensureAuthorized();
      setNeedsAuth(!authorized);
    })();
  }, [show]);

  // Reset call UI on every open/close so a previous call's "connected" state +
  // running timer never carry over the next time the dialer is shown.
  useEffect(() => {
    setStatus('idle');
    setMuted(false);
    setRecording(false);
    setElapsedMs(0);
    placedRef.current = false;
    setTransferPanel(false);
    setTransferTarget(null);
    setConsult(null);
    setTransferBusy(false);
    setSendingLink(false);
    consultTokenRef.current = null;
    setTimerId((id) => {
      if (id) clearInterval(id);
      return null;
    });
  }, [show]);

  const handleTerminated = useCallback(() => {
    setStatus('ended');
    if (timerId) clearInterval(timerId);
    if (typeof onHide === 'function') onHide(placedRef.current);
  }, [timerId, onHide]);

  useEffect(() => {
    return () => {
      offSessionEvent('terminated', handleTerminated);
      offSessionEvent('bye', handleTerminated);
    };
  }, [handleTerminated]);

  const placeCall = async () => {
    if (!targetNumber) return;
    const authorized = await ensureAuthorized();
    if (!authorized) {
      setNeedsAuth(true);
      return;
    }
    try {
      setNeedsAuth(false);
      setStatus('connecting');
      placedRef.current = true;
      if (typeof onCallPlaced === 'function') onCallPlaced(new Date());
      setAudioElements(remoteAudioRef.current, localAudioRef.current);
      const session = await startCallTo(targetNumber, fromNumber);
      onSessionEvent('terminated', handleTerminated);
      onSessionEvent('bye', handleTerminated);
      setStatus('connected');
      // Capture the RC telephony session id for the caller. The getter reads
      // the p-rc-api-ids SIP header, which only exists once the call
      // progresses (183/200) — so poll briefly, plus a last-chance read when
      // the session is disposed. Fires at most once; failures are silent
      // (the id is an enhancement, never required for the call itself).
      if (session && typeof onCallSessionId === 'function') {
        let fired = false;
        const readId = () => {
          try {
            return session.sessionId || null;
          } catch (_) {
            return null;
          }
        };
        const fire = () => {
          if (fired) return;
          const id = readId();
          if (id) {
            fired = true;
            onCallSessionId(id);
          }
        };
        let tries = 0;
        const poll = setInterval(() => {
          tries += 1;
          fire();
          if (fired || tries >= 40) clearInterval(poll);
        }, 500);
        try {
          session.on('disposed', () => {
            clearInterval(poll);
            fire();
          });
        } catch (_) {}
        fire();
      }
    } catch (e) {
      toast.error(e.message || 'Call failed');
      setStatus('failed');
    }
  };

  useEffect(() => {
    if (status === 'connected' && !timerId) {
      const t = setInterval(() => setElapsedMs((v) => v + 1000), 1000);
      setTimerId(t);
    }
    if (status !== 'connected' && timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  }, [status, timerId]);

  const formatDuration = (ms) => {
    const s = Math.floor(ms / 1000);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return hh > 0
      ? `${pad(hh)}:${pad(mm)}:${pad(ss)}`
      : `${pad(mm)}:${pad(ss)}`;
  };

  // Closing the dialer (X or Hang Up) must actually end the call, not just hide
  // the modal — otherwise the audio keeps playing with no way to stop it.
  const requestClose = () => {
    try {
      hangupActiveCall();
    } catch (_) {}
    if (typeof onHide === 'function') onHide(placedRef.current);
  };

  // Keep the screen awake while a call is live: WebRTC audio runs in the tab,
  // and (especially on phones) the display sleeping is the main way audio
  // gets suspended mid-call. Silently skipped where unsupported.
  useEffect(() => {
    if (status !== 'connecting' && status !== 'connected') return undefined;
    let lock = null;
    let disposed = false;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock?.request?.('screen');
      } catch (_) {}
    };
    const onVisibility = () => {
      // The OS releases the lock when the tab backgrounds; re-acquire on return.
      if (!disposed && document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try {
        lock?.release?.();
      } catch (_) {}
    };
  }, [status]);

  const toggleMute = () => {
    try {
      if (isLocalAudioMuted()) {
        unmuteLocalAudio();
        setMuted(false);
      } else {
        muteLocalAudio();
        setMuted(true);
      }
    } catch (_) {}
  };

  // ---- Live transfer (2026-08-10) ----

  const beginBlind = async (target) => {
    setTransferBusy(true);
    try {
      // Stage the parent's confirm BEFORE the REFER: our leg terminates the
      // instant the transfer lands, and the context must already be there.
      if (typeof onTransferred === 'function') onTransferred(target, 'blind');
      await transferActiveCall(target.extension);
    } catch (e) {
      if (typeof onTransferred === 'function') onTransferred(null);
      toast.error(e?.message || 'Transfer failed');
    } finally {
      setTransferBusy(false);
      setTransferPanel(false);
      setTransferTarget(null);
    }
  };

  const beginWarm = async (target) => {
    setTransferBusy(true);
    try {
      const { complete, cancel, newSession } = await warmTransferActiveCall(
        target.extension,
      );
      const token = {};
      consultTokenRef.current = token;
      setConsult({ target, complete, cancel });
      try {
        newSession.on('disposed', () => {
          // The target hung up / declined before Complete: return to the
          // caller automatically. A completed or superseded consult is a
          // no-op here (token mismatch).
          if (consultTokenRef.current !== token) return;
          consultTokenRef.current = null;
          Promise.resolve(cancel()).catch(() => {});
          setConsult(null);
          toast.info(`${target.name} didn't pick up. Back to the caller.`);
        });
      } catch (_) {}
    } catch (e) {
      toast.error(e?.message || 'Could not start the transfer');
    } finally {
      setTransferBusy(false);
      setTransferPanel(false);
      setTransferTarget(null);
    }
  };

  const completeWarm = async () => {
    if (!consult) return;
    setTransferBusy(true);
    try {
      consultTokenRef.current = null;
      if (typeof onTransferred === 'function') {
        onTransferred(consult.target, 'warm');
      }
      await consult.complete();
      // Both legs join; our session disposes -> handleTerminated closes us.
    } catch (e) {
      if (typeof onTransferred === 'function') onTransferred(null);
      toast.error(e?.message || 'Could not complete the transfer');
    } finally {
      setTransferBusy(false);
    }
  };

  const cancelWarm = async () => {
    if (!consult) return;
    consultTokenRef.current = null;
    setTransferBusy(true);
    try {
      await consult.cancel();
    } catch (_) {}
    setConsult(null);
    setTransferBusy(false);
  };

  const doSendLink = async () => {
    if (typeof onSendLink !== 'function' || sendingLink) return;
    setSendingLink(true);
    try {
      await onSendLink();
    } catch (_) {
      /* the parent's mutation surfaces the error */
    } finally {
      setSendingLink(false);
    }
  };

  // ---- Docked call-bar presentation (dockOnNarrow, <1100px) ----
  if (docked) {
    if (!show) return null;
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-[1075] border-t border-[#E3E8EF] bg-white px-3 pt-2 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] dark:border-white/10 dark:!bg-surface-elevated"
        style={{
          paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
        }}
      >
        {/* Audio elements live in the bar itself so the stream always has a
            mounted sink regardless of call state. */}
        <audio ref={remoteAudioRef} autoPlay />
        <audio ref={localAudioRef} autoPlay muted />
        <div className="flex items-center gap-3">
          {unsupported ? (
            <>
              <span className="min-w-0 flex-1 text-xs leading-snug text-slate-600 dark:text-slate-300">
                {UNSUPPORTED_MSG}
              </span>
              <button
                type="button"
                onClick={requestClose}
                aria-label="Close dialer"
                className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                ✕
              </button>
            </>
          ) : needsAuth ? (
            <>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-600 dark:text-slate-300">
                Login to RingCentral to place calls
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    // Popup first (2026-08-18): the redirect flow drops the
                    // whole page (open panels, drafts) for a login the popup
                    // handles in place. Redirect survives only as the
                    // popup-blocked fallback, with the pending-number
                    // recovery it always had.
                    const ok = await openAuthorizePopup();
                    if (ok) {
                      setNeedsAuth(false);
                      return;
                    }
                    rememberPendingNumber(targetNumber);
                    await redirectToAuthorize();
                  } catch (e) {
                    toast.error(
                      e.message || 'Could not start RingCentral login',
                    );
                  }
                }}
                className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white dark:bg-slate-200 dark:!text-slate-900"
              >
                Login
              </button>
            </>
          ) : status === 'idle' ? (
            <>
              <FontAwesomeIcon
                icon={faPhone}
                className="shrink-0 text-slate-400"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {targetDisplay}
              </span>
              <button
                type="button"
                onClick={placeCall}
                disabled={!targetNumber || isPlacing}
                className="shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 dark:bg-slate-200 dark:!text-slate-900"
              >
                Place call now
              </button>
              <button
                type="button"
                onClick={requestClose}
                aria-label="Close dialer"
                className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                {status === 'connected' ? (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                ) : null}
                <span
                  className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                    status === 'connected'
                      ? 'bg-emerald-500'
                      : status === 'connecting'
                        ? 'bg-amber-400'
                        : 'bg-slate-300'
                  }`}
                />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {targetDisplay}
              </span>
              <span className="shrink-0 font-mono text-sm tabular-nums text-slate-600 dark:text-slate-300">
                {status === 'connected' ? formatDuration(elapsedMs) : status}
              </span>
              {status === 'connected' || status === 'connecting' ? (
                <>
                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={muted ? 'Unmute' : 'Mute'}
                    className={`shrink-0 rounded-md p-2 ${
                      muted
                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300'
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={muted ? faMicrophoneSlash : faMicrophone}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={isPlacing}
                    aria-label="Hang up"
                    className="shrink-0 rounded-md bg-red-500 p-2 text-white disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faPhoneSlash} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={requestClose}
                  aria-label="Close dialer"
                  className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  ✕
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // The shared RC login prompt (2026-08-22): the exact modal this component
  // used to render inline, now shared verbatim with the text composer. All
  // Modal passthroughs ride along so the board's z-index layering holds.
  if (needsAuth && !unsupported) {
    return (
      <RcLoginModal
        show={show}
        onHide={requestClose}
        onLoggedIn={() => setNeedsAuth(false)}
        pendingNumber={targetNumber}
        note={
          !hideRecord ? 'Calls must be recorded to collect transcript.' : null
        }
        backdrop={backdrop}
        style={style}
        className={className}
        dialogClassName={dialogClassName}
        enforceFocus={enforceFocus}
      />
    );
  }

  return (
    <Modal
      show={show}
      onHide={requestClose}
      backdrop={backdrop}
      centered
      style={style}
      className={className}
      dialogClassName={dialogClassName}
      enforceFocus={enforceFocus}
    >
      {/* Slim header: icon + close only — the number renders LARGE in each
          body view (idle and in-call), so repeating it here read twice. */}
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPhone} className="text-slate-400" />
        </Modal.Title>
      </Modal.Header>
      {unsupported ? (
        /* Same body-only Tailwind language as the in-call view — the Bootstrap
           Body/Footer split read as harsh divider lines, worst in dark. */
        <Modal.Body className="p-0">
          <div className="flex flex-col items-center gap-4 px-5 pb-6 pt-4">
            <p className="mb-0 max-w-xs text-center text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {UNSUPPORTED_MSG}
            </p>
            <button
              type="button"
              onClick={requestClose}
              className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      ) : status === 'idle' ? (
        /* Pre-call: mirror of the in-call layout — the big green circle IS
           the action, native-dialer style. */
        <Modal.Body className="p-0">
          <div className="flex flex-col items-center gap-4 px-5 pb-6 pt-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:bg-white/10 dark:text-slate-400">
              Ready to call
            </span>
            <div className="font-mono text-3xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-slate-100">
              {targetDisplay}
            </div>
            {!hideRecord && (
              <p className="-mt-2 mb-0 text-center text-xs text-slate-400 dark:text-slate-500">
                Calls must be recorded to collect transcript.
              </p>
            )}
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                onClick={placeCall}
                disabled={!targetNumber || isPlacing}
                aria-label="Place call now"
                className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)] transition-colors hover:bg-emerald-400 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPhone} size="lg" />
              </button>
              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                {isPlacing ? 'Calling…' : 'Place call'}
              </span>
            </div>
          </div>
        </Modal.Body>
      ) : (
        /* In-call view (2026-08-06 restyle): Tailwind pass consistent with
           the board — status pill with a live pulse, big tabular timer,
           circular touch controls. The Modal shell + all SIP logic are
           untouched; only the presentation changed. */
        <Modal.Body className="p-0">
          <div className="flex flex-col items-center gap-4 px-5 pb-6 pt-4">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
                status === 'connected'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                  : status === 'failed'
                    ? 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400'
                    : status === 'ended'
                      ? 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400'
                      : 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
              }`}
            >
              <span className="relative flex h-2 w-2">
                {status === 'connected' ? (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                ) : null}
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${
                    status === 'connected'
                      ? 'bg-emerald-500'
                      : status === 'failed'
                        ? 'bg-red-500'
                        : status === 'ended'
                          ? 'bg-slate-400'
                          : 'bg-amber-400'
                  }`}
                />
              </span>
              {status === 'connected'
                ? 'Connected'
                : status === 'failed'
                  ? 'Failed'
                  : status === 'ended'
                    ? 'Ended'
                    : 'Connecting'}
            </span>

            <div className="font-mono text-5xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-slate-100">
              {formatDuration(elapsedMs)}
            </div>
            <div className="-mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">
              {targetDisplay}
            </div>

            {/* Warm-consult state: the caller is on hold while we talk to
                the transfer target — Complete/Cancel replace the normal
                control row until this resolves. */}
            {consult ? (
              <div className="w-full max-w-xs rounded-xl bg-violet-50 p-3 text-center dark:bg-violet-500/10">
                <div className="mb-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
                  Caller on hold · talking with {consult.target.name}
                </div>
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    disabled={transferBusy}
                    onClick={completeWarm}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    Complete transfer
                  </button>
                  <button
                    type="button"
                    disabled={transferBusy}
                    onClick={cancelWarm}
                    className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:!bg-white/10 dark:text-slate-300 dark:hover:!bg-white/15"
                  >
                    Cancel and return
                  </button>
                </div>
              </div>
            ) : null}

            {/* Transfer target picker: pick a person, then Send now (blind)
                or Ask first (warm consult). */}
            {!consult && transferPanel ? (
              <div className="w-full max-w-xs rounded-xl bg-slate-50 p-3 dark:bg-white/5">
                {!transferTarget ? (
                  <>
                    <div className="mb-2 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Transfer to
                    </div>
                    <div className="space-y-1.5">
                      {transferTargets.map((t) => (
                        <button
                          key={t.userId}
                          type="button"
                          disabled={transferBusy}
                          onClick={() => setTransferTarget(t)}
                          className="flex w-full items-center justify-between rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15"
                        >
                          {t.name}
                          <span className="text-[10px] font-medium text-slate-400">
                            ext {t.extension}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-2 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Transfer to {transferTarget.name}?
                    </div>
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        disabled={transferBusy}
                        onClick={() => beginWarm(transferTarget)}
                        title="Put the caller on hold and talk to them first"
                        className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
                      >
                        Ask first
                      </button>
                      <button
                        type="button"
                        disabled={transferBusy}
                        onClick={() => beginBlind(transferTarget)}
                        title="Forward the caller right now; your leg ends"
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                      >
                        Send now
                      </button>
                    </div>
                  </>
                )}
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (transferTarget) setTransferTarget(null);
                      else setTransferPanel(false);
                    }}
                    className="text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {transferTarget ? 'Back' : 'Cancel'}
                  </button>
                </div>
              </div>
            ) : null}

            {consult ? null : (
              <div className="mt-1 flex items-start gap-5">
                {!hideRecord && (
                  <div className="flex flex-col items-center gap-1.5">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (!recording) {
                            await startCallRecording();
                            setRecording(true);
                          } else {
                            await stopCallRecording();
                            setRecording(false);
                          }
                        } catch (e) {
                          toast.error(e?.message || 'Record failed');
                        }
                      }}
                      disabled={isPlacing || status !== 'connected'}
                      aria-label={recording ? 'Stop recording' : 'Record'}
                      className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
                        recording
                          ? 'bg-red-500 text-white'
                          : 'bg-slate-100 text-red-500 hover:bg-slate-200 dark:bg-white/10 dark:text-red-400 dark:hover:bg-white/15'
                      }`}
                    >
                      <FontAwesomeIcon icon={recording ? faStop : faCircle} />
                    </button>
                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      {recording ? 'Stop' : 'Record'}
                    </span>
                  </div>
                )}
                <div className="flex flex-col items-center gap-1.5">
                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={muted ? 'Unmute' : 'Mute'}
                    className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                      muted
                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15'
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={muted ? faMicrophoneSlash : faMicrophone}
                    />
                  </button>
                  <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    {muted ? 'Unmute' : 'Mute'}
                  </span>
                </div>
                {typeof onSendLink === 'function' && status === 'connected' ? (
                  <div className="flex flex-col items-center gap-1.5">
                    <button
                      type="button"
                      onClick={doSendLink}
                      disabled={!sendLinkEnabled || sendingLink}
                      aria-label="Send the scheduling link"
                      title={
                        sendLinkEnabled
                          ? 'Text them the Calendly link so they can book at their convenience'
                          : 'No scheduling link is set. A manager can add it on the Schedule calendar.'
                      }
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-40 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      {sendingLink ? 'Sending…' : 'Calendly'}
                    </span>
                  </div>
                ) : null}
                {transferTargets.length > 0 && status === 'connected' ? (
                  <div className="flex flex-col items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setTransferPanel((v) => !v)}
                      disabled={transferBusy}
                      aria-label="Transfer the call"
                      title="Transfer this call to an onboarding person"
                      className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
                        transferPanel
                          ? 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15'
                      }`}
                    >
                      <FontAwesomeIcon icon={faRightLeft} />
                    </button>
                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      Transfer
                    </span>
                  </div>
                ) : null}
                <div className="flex flex-col items-center gap-1.5">
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={isPlacing}
                    aria-label="Hang up"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-white shadow-[0_2px_8px_rgba(239,68,68,0.35)] transition-colors hover:bg-red-600 disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faPhoneSlash} />
                  </button>
                  <span className="text-[11px] font-semibold text-red-500">
                    Hang Up
                  </span>
                </div>
              </div>
            )}

            <audio ref={remoteAudioRef} autoPlay />
            <audio ref={localAudioRef} autoPlay muted />
          </div>
        </Modal.Body>
      )}
    </Modal>
  );
};

export default RcCallModal;
